import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// ─── Integration type metadata ────────────────────────────────────────────────
const TYPES = {
  google_calendar: {
    label: 'Google Calendar',
    icon: '📅',
    description: 'Let the bot check your meetings and upcoming events.',
    fields: [
      { key: 'label',              label: 'Display Name',        placeholder: 'e.g. Work Calendar', type: 'text', required: true, meta: false },
      { key: 'serviceAccountJson', label: 'Service Account JSON', placeholder: 'Paste the full JSON from Google Cloud Console…', type: 'textarea', required: true, meta: true, secret: true },
      { key: 'calendarId',         label: 'Calendar ID',          placeholder: 'primary (or paste calendar ID)', type: 'text', required: false, meta: false, configKey: true },
    ],
    hint: 'Go to Google Cloud Console → IAM → Service Accounts → Create key (JSON). Share your Google Calendar with the service account email.',
  },
  hubspot: {
    label: 'HubSpot CRM',
    icon: '🟠',
    description: 'Search contacts, leads, and deal stages from HubSpot.',
    fields: [
      { key: 'label',  label: 'Display Name', placeholder: 'e.g. Main CRM',       type: 'text', required: true, meta: false },
      { key: 'apiKey', label: 'Private App Token', placeholder: 'pat-na1-…', type: 'password', required: true, meta: true, secret: true },
    ],
    hint: 'HubSpot → Settings → Integrations → Private Apps → Create. Scopes needed: crm.objects.contacts.read',
  },
  custom_api: {
    label: 'Custom API',
    icon: '🔌',
    description: 'Connect any HTTP endpoint — your own CRM, database, or internal tool.',
    fields: [
      { key: 'label',           label: 'Display Name',     placeholder: 'e.g. My CRM',        type: 'text',     required: true,  meta: false },
      { key: 'toolName',        label: 'Tool Name (AI)',   placeholder: 'search_leads',        type: 'text',     required: true,  meta: false, toolField: true },
      { key: 'toolDescription', label: 'Tool Description', placeholder: 'Search for leads in my CRM by name or email', type: 'textarea', required: true, meta: false, toolField: true },
      { key: 'baseUrl',         label: 'Base URL',         placeholder: 'https://api.mycrm.com/v1/contacts', type: 'text', required: true, meta: false, configKey: true },
      { key: 'method',          label: 'HTTP Method',      type: 'select', options: ['GET','POST','PUT','PATCH'], required: false, meta: false, configKey: true },
      { key: 'authType',        label: 'Auth Type',        type: 'select', options: ['none','bearer','api_key_header','basic'], required: false, meta: false, configKey: true },
      { key: 'apiKey',          label: 'API Key / Token',  placeholder: 'your-api-key',        type: 'password', required: false, meta: true, secret: true },
      { key: 'username',        label: 'Username (Basic)', placeholder: '',                    type: 'text',     required: false, meta: true },
      { key: 'password',        label: 'Password (Basic)', placeholder: '',                    type: 'password', required: false, meta: true, secret: true },
    ],
    hint: 'Tool Name must be snake_case (e.g. search_leads). Use {param} in URL for path parameters. The AI will call this with the parameters you define below.',
    hasParams: true,
  },
};

const DEFAULT_PARAMS = JSON.stringify({
  type: 'object',
  properties: {
    query: { type: 'string', description: 'Search query or keyword' },
  },
  required: ['query'],
}, null, 2);

// ─── Modal ────────────────────────────────────────────────────────────────────
function IntegrationModal({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [step, setStep]           = useState(isEdit ? 'form' : 'type');
  const [selectedType, setType]   = useState(existing?.type || '');
  const [form, setForm]           = useState({});
  const [params, setParams]       = useState(DEFAULT_PARAMS);
  const [paramsError, setParamsError] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (isEdit && existing) {
      const f = { label: existing.label, method: existing.config?.method || 'GET', authType: existing.config?.authType || 'none' };
      if (existing.config?.calendarId) f.calendarId = existing.config.calendarId;
      if (existing.config?.baseUrl)    f.baseUrl    = existing.config.baseUrl;
      if (existing.config?.authHeader) f.authHeader = existing.config.authHeader;
      if (existing.toolName)           f.toolName        = existing.toolName;
      if (existing.toolDescription)    f.toolDescription = existing.toolDescription;
      setForm(f);
      if (existing.toolParameters) setParams(JSON.stringify(existing.toolParameters, null, 2));
    }
  }, [isEdit, existing]);

  const typeMeta = TYPES[selectedType];

  function set(key, val) { setForm(p => ({ ...p, [key]: val })); }

  async function save() {
    setError('');
    setSaving(true);
    try {
      const credentials = {};
      const config = {};

      typeMeta.fields.forEach(f => {
        if (f.secret || f.meta) {
          if (form[f.key]) credentials[f.key] = form[f.key];
        } else if (f.configKey) {
          if (form[f.key]) config[f.key] = form[f.key];
        }
      });

      // For custom_api, validate toolParameters JSON
      let toolParameters;
      if (selectedType === 'custom_api') {
        try {
          toolParameters = JSON.parse(params);
          setParamsError('');
        } catch {
          setParamsError('Tool Parameters must be valid JSON');
          setSaving(false);
          return;
        }
      }

      const payload = {
        type: selectedType,
        label: form.label,
        credentials,
        config,
        ...(selectedType === 'custom_api' ? {
          toolName:        form.toolName,
          toolDescription: form.toolDescription,
          toolParameters,
        } : {}),
      };

      if (isEdit) {
        await api.put(`/api/integrations/${existing._id}`, payload);
      } else {
        await api.post('/api/integrations', payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  }

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={m.modal}>
        <div style={m.header}>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>
            {isEdit ? `Edit: ${existing.label}` : 'Add Integration'}
          </span>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Step 1: choose type */}
        {step === 'type' && (
          <div style={{ padding: '20px 24px' }}>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>What do you want to connect?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(TYPES).map(([type, meta]) => (
                <button
                  key={type}
                  onClick={() => { setType(type); setStep('form'); }}
                  style={m.typeCard}
                >
                  <span style={{ fontSize: 22 }}>{meta.icon}</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{meta.label}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{meta.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: form */}
        {step === 'form' && typeMeta && (
          <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '70vh' }}>
            {/* Hint box */}
            <div style={m.hint}>{typeMeta.hint}</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
              {typeMeta.fields.map(f => {
                // Hide basic auth fields unless authType=basic
                if ((f.key === 'username' || f.key === 'password') && form.authType !== 'basic') return null;
                // Hide apiKey if authType=none
                if (f.key === 'apiKey' && form.authType === 'none') return null;

                return (
                  <div key={f.key}>
                    <label style={m.label}>
                      {f.label}{f.required && <span style={{ color: '#f87171' }}> *</span>}
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea
                        rows={f.key === 'serviceAccountJson' ? 6 : 3}
                        value={form[f.key] || ''}
                        onChange={e => set(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        style={{ ...m.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 11 }}
                      />
                    ) : f.type === 'select' ? (
                      <select value={form[f.key] || f.options[0]} onChange={e => set(f.key, e.target.value)} style={m.input}>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={f.type}
                        value={form[f.key] || ''}
                        onChange={e => set(f.key, e.target.value)}
                        placeholder={isEdit && f.secret ? '(unchanged — leave blank to keep)' : (f.placeholder || '')}
                        style={m.input}
                      />
                    )}
                  </div>
                );
              })}

              {/* Tool Parameters (custom_api only) */}
              {typeMeta.hasParams && (
                <div>
                  <label style={m.label}>Tool Parameters (JSON Schema)</label>
                  <p style={{ color: '#64748b', fontSize: 11, marginBottom: 6 }}>
                    Define what arguments the AI can pass to this API. Must be a valid JSON Schema object.
                  </p>
                  <textarea
                    rows={8}
                    value={params}
                    onChange={e => setParams(e.target.value)}
                    style={{ ...m.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 11 }}
                  />
                  {paramsError && <p style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>{paramsError}</p>}
                </div>
              )}
            </div>

            {error && <p style={{ color: '#f87171', fontSize: 13, marginTop: 12 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {!isEdit && <button style={m.backBtn} onClick={() => setStep('type')}>← Back</button>}
              <button style={m.saveBtn} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Update' : 'Connect'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Integrations() {
  const { user } = useAuth();
  const isOrgAdmin = user?.role === 'orgadmin';
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(null); // null | 'add' | { edit: integration }
  const [testing, setTesting]           = useState(null); // id being tested
  const [testResult, setTestResult]     = useState({}); // id → { ok, text }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/integrations');
      setIntegrations(data.integrations || []);
    } catch { setIntegrations([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleEnabled(integ) {
    try {
      await api.put(`/api/integrations/${integ._id}`, { enabled: !integ.enabled });
      load();
    } catch {}
  }

  async function deleteInteg(id) {
    if (!window.confirm('Remove this integration? The bot will lose access to it immediately.')) return;
    await api.delete(`/api/integrations/${id}`);
    load();
  }

  async function testInteg(id) {
    setTesting(id);
    setTestResult(p => ({ ...p, [id]: null }));
    try {
      const { data } = await api.post(`/api/integrations/${id}/test`);
      setTestResult(p => ({ ...p, [id]: { ok: true, text: data.result?.slice(0, 200) || 'Connected!' } }));
    } catch (err) {
      setTestResult(p => ({ ...p, [id]: { ok: false, text: err.response?.data?.error || 'Test failed' } }));
    }
    setTesting(null);
  }

  const TYPE_META = TYPES;

  return (
    <div style={{ padding: 24, background: '#0f172a', minHeight: '100vh', color: '#f1f5f9' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Integrations</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>
            Connect tools your WhatsApp bot can use during conversations — calendars, CRMs, custom APIs, and more.
          </p>
        </div>
        {isOrgAdmin && (
          <button style={s.addBtn} onClick={() => setModal('add')}>+ Add Integration</button>
        )}
      </div>

      {/* Active integrations */}
      {loading ? (
        <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>Loading…</div>
      ) : integrations.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔌</div>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>No integrations connected</p>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            {isOrgAdmin
              ? 'Connect a calendar, CRM, or custom API so your bot can answer questions using live data.'
              : 'No integrations have been set up by your org admin yet.'}
          </p>
        </div>
      ) : (
        <div style={s.grid}>
          {integrations.map(integ => {
            const meta = TYPE_META[integ.type] || {};
            const tr = testResult[integ._id];
            return (
              <div key={integ._id} style={{ ...s.card, opacity: integ.enabled ? 1 : 0.55 }}>
                <div style={s.cardHeader}>
                  <span style={{ fontSize: 22 }}>{meta.icon || '🔧'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9' }}>{integ.label}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{meta.label || integ.type}</div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                    background: integ.enabled ? '#14532d' : '#1e293b',
                    color:      integ.enabled ? '#4ade80' : '#64748b',
                  }}>
                    {integ.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>

                {integ.type === 'custom_api' && integ.config?.baseUrl && (
                  <div style={s.urlTag}>{integ.config.baseUrl.slice(0, 60)}{integ.config.baseUrl.length > 60 ? '…' : ''}</div>
                )}

                {tr && (
                  <div style={{ ...s.testBadge, background: tr.ok ? '#14532d' : '#2a1f1f', color: tr.ok ? '#4ade80' : '#f87171' }}>
                    {tr.ok ? '✓ ' : '✗ '}{tr.text}
                  </div>
                )}

                {isOrgAdmin && (
                  <div style={s.cardActions}>
                    <button style={s.actionBtn} onClick={() => setModal({ edit: integ })}>Edit</button>
                    <button
                      style={s.actionBtn}
                      onClick={() => testInteg(integ._id)}
                      disabled={testing === integ._id}
                    >
                      {testing === integ._id ? 'Testing…' : 'Test'}
                    </button>
                    <button style={s.actionBtn} onClick={() => toggleEnabled(integ)}>
                      {integ.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button style={{ ...s.actionBtn, color: '#f87171' }} onClick={() => deleteInteg(integ._id)}>Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Web search note */}
      <div style={s.webSearchNote}>
        <span style={{ fontSize: 18 }}>🌐</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Web Search</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Always on when Tavily API key is configured. The bot automatically searches the web when it doesn't have an answer.</div>
        </div>
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#1e293b', color: '#94a3b8' }}>Built-in</span>
      </div>

      {/* Modal */}
      {modal === 'add' && (
        <IntegrationModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.edit && (
        <IntegrationModal existing={modal.edit} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  addBtn: { background: '#6366f1', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  emptyState: { textAlign: 'center', padding: '60px 24px', background: '#1e293b', borderRadius: 12, border: '1px solid #334155' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 12 },
  urlTag: { fontSize: 11, color: '#475569', fontFamily: 'monospace', background: '#0f172a', padding: '4px 8px', borderRadius: 6 },
  testBadge: { fontSize: 12, padding: '6px 10px', borderRadius: 6 },
  cardActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  actionBtn: { background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  webSearchNote: { marginTop: 32, display: 'flex', alignItems: 'center', gap: 14, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 18 },
};

const m = {
  overlay: { position: 'fixed', inset: 0, background: '#00000088', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#1e293b', border: '1px solid #334155', borderRadius: 14, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #334155' },
  closeBtn: { background: 'transparent', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer' },
  typeCard: { display: 'flex', alignItems: 'center', gap: 14, background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', width: '100%' },
  hint: { background: '#0f172a', border: '1px solid #1e40af44', color: '#93c5fd', fontSize: 12, padding: '10px 14px', borderRadius: 8 },
  label: { display: 'block', fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 5 },
  input: { width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '9px 12px', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' },
  saveBtn: { flex: 1, background: '#6366f1', color: '#fff', border: 'none', padding: '10px 0', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  backBtn: { background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
};
